import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs"

import { Alert, Box, Grid, styled, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';

import TextFieldTheme from "../../../../shared/general/TextFieldTheme";

import LeaveTypeForm from "./leaveTypeForm";
import ButtonBlue from "../../../../shared/general/ButtonBlue";
import { getAllGroupLevelByIdVendor } from "../../../../../../actions/level";
import { getEmployeeTypeByVendorId } from "../../../../../../actions/vendor";
import { createLeaveGroup, updateLeaveGroup } from "../../../../../../actions/leave";
import { openNotificationAlert } from "../../../../../../actions/notificationAlert";
import StyledToggleButtonGroup from "../../../../shared/general/ToggleButtonGroupStyled";
import { Fragment } from "react";
import DatePickerCustom from "../../../../shared/date/datePicker";

const StyledRoot = styled(Box)({
  padding: "16px",
  "& .MuiAutocomplete-root": {
    "& .MuiOutlinedInput-root": {
      padding: "13.5px 14px",
      paddingRight: "32px",
      "& input": {
        padding: 0,
      },
    },
  },
  "& .field-label": {
    paddingBottom: "4px",
    fontWeight: "500",
    fontSize: "14px",
  },
  "& .header-leave-type": {
    paddingLeft: "8px",
    borderLeft: "8px solid #1976D2",
    borderTopLeftRadius: "4px",
    borderBottomLeftRadius: "4px",
  }
});

const LeaveForm = (props) => {

  const { drawerConfig, onClose, fetchingAllLeaveGroup } = props;

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const { result: userProfile } = useSelector((state) => state.userProfile);

  const [groupLevelList, setGroupLevelList] = useState([]);
  const [employeeTypeList, setEmployeeTypeList] = useState([]);

  const [deletedLeaveTypeIdList, setDeletedLeaveTypeIdList] = useState([]);

  const validateYupSchema = yup.object({
    leaveGroupName: yup.string().required(t("ThisFieldIsRequired")),
    leaveGroupName_EN: yup.string().required(t("ThisFieldIsRequired")),
    leaveTypeList: yup.array().of(
      yup.object().shape({
        idEmployeeTypeList: yup.array().of(yup.string()).min(1, t("ThisFieldIsRequired")),
        idPersonnelLevelGroupList: yup.array().of(yup.string()).min(1, t("ThisFieldIsRequired")),
        isPaid: yup.string().required(t("ThisFieldIsRequired")),
        canRequestAfter: yup.string().required(t("ThisFieldIsRequired")),
        gender: yup.string().required(t("ThisFieldIsRequired")),
        minLeave: yup.string().required(t("ThisFieldIsRequired")),
        isProRate: yup.bool().required(t("ThisFieldIsRequired")),
        proRateRound: yup.string().required(t("ThisFieldIsRequired")),
        isLeaveStep: yup.string().required(t("ThisFieldIsRequired")),
      })
    ).min(1),
    effectiveYear: yup.date().nullable().when("editMode", {
      is: (val) => val === "effective",
      then: (schema) =>
        schema.required(t("ThisFieldIsRequired"))
    }),
  })

  const useHookForm = useForm({
    defaultValues: {
      editMode: "effective",
      effectiveYear: null,
      leaveGroupName: "",
      leaveGroupName_EN: "",
      leaveTypeList: [
        {
          idLeaveType: null,
          idEmployeeTypeList: ['all'],
          idPersonnelLevelGroupList: ['all'],
          leaveStepList: [],
          isPaid: 1,
          canRequestAfter: 0,
          gender: "all",
          minLeave: 10,
          dayInAdvance: "",
          isProRate: false,
          proRateRound: 1,
          isLeaveStep: 0,
          leaveValue: "",
          carryValue: "",
        }
      ],
    },
    resolver: yupResolver(validateYupSchema),
    mode: "all",
  })

  const useFieldArrayLeaveType = useFieldArray({
    control: useHookForm.control,
    name: "leaveTypeList",
  })

  const handleInsertLeaveType = () => {
    useFieldArrayLeaveType.append({
      idLeaveType: null,
      idEmployeeTypeList: ['all'],
      idPersonnelLevelGroupList: ['all'],
      leaveStepList: [],
      isPaid: 1,
      canRequestAfter: 0,
      gender: "all",
      minLeave: 10,
      dayInAdvance: "",
      isProRate: false,
      proRateRound: 1,
      isLeaveStep: 0,
      leaveValue: "",
      carryValue: "",
    })
  }

  const handleRemoveLeaveType = (index, idLeaveType) => {
    useFieldArrayLeaveType.remove(index);
    if(idLeaveType){
      setDeletedLeaveTypeIdList([...deletedLeaveTypeIdList, idLeaveType])
    }
  }

  const handleMoveUpLeaveType = (index) => {
    useFieldArrayLeaveType.move(index, index - 1);
  }

  const handleMoveDownLeaveType = (index) => {
    useFieldArrayLeaveType.move(index, index + 1);
  }

  const onSubmit = async (data) => {

    const formData = {...data};
    Object.keys(formData).map(key => {
      if(formData[key] === ""){
        formData[key] = null;
      }
    })

    if(formData.effectiveYear){
      formData.effectiveYear = dayjs(data.effectiveYear).format("YYYY-MM-DD");
    }

    let result = null;

    if(drawerConfig.isEdit){
      formData.deletedLeaveTypeIdList = deletedLeaveTypeIdList;
      result = await dispatch(updateLeaveGroup(formData))

    } else {
      result = await dispatch(createLeaveGroup(formData));
    }

    if(result && result.status === 200) {
      dispatch(openNotificationAlert({
        type: "success",
        message: t("DataSaveSuccessful"),
      }));
      fetchingAllLeaveGroup();
      onClose();
    } else {
      dispatch(openNotificationAlert({
        type: "error",
        message: t("AnErrorOccurred")
      }));
    }
  }

  const fetchingGroupLevelList = async () => {
    const result = await dispatch(getAllGroupLevelByIdVendor(userProfile.idVendor));
    if(result && result.status === 200){
      setGroupLevelList(result.data);
    } else {
      setGroupLevelList([]);
    }
  }

  const fetchingEmployeeTypeList = async () => {
    const result = await dispatch(getEmployeeTypeByVendorId(userProfile.idVendor));
    if(result && result.status === 200){
      setEmployeeTypeList(result.data);
    } else {
      setEmployeeTypeList([])
    }
  }

  useEffect(() => {
    if(userProfile){
      fetchingGroupLevelList();
      fetchingEmployeeTypeList();
    }
  }, [userProfile])

  const checkIfNull = (value) => {
    if(value === null){
      return ""
    } else {
      return value;
    }
  }

  useEffect(() => {
    if(drawerConfig.isOpen){

      if(drawerConfig.isEdit){
        
        const leaveTypeList = [];
        drawerConfig.data.leaveTypeList.map(leaveType => {

          const leaveStepList = [];

          leaveType.leaveStepList.map(step => {

            leaveStepList.push({
              conditionType: "SERV",
              lessThanYear: step.lessThanYear,
              moreThanYears: step.moreThanYears,
              leaveValue: checkIfNull(step.leaveValue),
              carryValue: checkIfNull(step.carryValue),
            })

          })

          leaveTypeList.push({
            idLeaveType: leaveType.idLeaveType,
            idEmployeeTypeList: leaveType.employeeTypeList.length === 0? ["all"]: leaveType.employeeTypeList.map(x => x.idEmployeeType),
            idPersonnelLevelGroupList: leaveType.personnelLevelGroupList.length === 0? ["all"]: leaveType.personnelLevelGroupList.map(x => x.idGroupLevel),
            leaveStepList: leaveStepList,
            isPaid: checkIfNull(leaveType.isPaid),
            canRequestAfter: checkIfNull(leaveType.canRequestAfter),
            gender: "all",
            minLeave: checkIfNull(leaveType.minLeave),
            dayInAdvance: checkIfNull(leaveType.dayInAdvance),
            isProRate: Boolean(leaveType.isProRate),
            proRateRound: checkIfNull(leaveType.proRateRound),
            isLeaveStep: leaveType.isLeaveStep,
            leaveValue: checkIfNull(leaveType.leaveValue),
            carryValue: checkIfNull(leaveType.carryValue),
          })
        })

        useHookForm.reset({
          editMode: "overwrite",
          effectiveYear: null,
          idLeaveGroup: drawerConfig.data.idLeaveGroup,
          leaveGroupName: drawerConfig.data.leaveGroupName || "",
          leaveGroupName_EN: drawerConfig.data.leaveGroupName_EN || "",
          leaveTypeList: leaveTypeList,
        })


      } else {
        useHookForm.reset({
          editMode: "effective",
          effectiveYear: null,
          leaveGroupName: "",
          leaveGroupName_EN: "",
          leaveTypeList: [
            {
              idLeaveType: null,
              idEmployeeTypeList: ['all'],
              idPersonnelLevelGroupList: ['all'],
              leaveStepList: [],
              isPaid: 1,
              canRequestAfter: 0,
              gender: "all",
              minLeave: 10,
              dayInAdvance: "",
              isProRate: false,
              proRateRound: 1,
              isLeaveStep: 0,
              leaveValue: "",
              carryValue: "",
            }
          ],
        })
      }

    }
  }, [drawerConfig.isOpen])

  const editModeWatch = useHookForm.watch("editMode");
  
  return (
    <StyledRoot>
      <form onSubmit={useHookForm.handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {drawerConfig.isEdit && <Grid item xs={12}>
            <Controller
              name="editMode"
              control={useHookForm.control}
              render={({field}) => (
                <StyledToggleButtonGroup
                  {...field}
                  exclusive
                  color="primary"
                >
                  <ToggleButton value="overwrite">
                    {t("Leave.Overwrite")}
                  </ToggleButton>
                  <ToggleButton value="effective">
                    {t("Leave.SetEffectiveDate")}
                  </ToggleButton>
                </StyledToggleButtonGroup>
              )}
            />
          </Grid>}

          {editModeWatch === "effective" && (
            <Fragment>
              {drawerConfig.isEdit && <Grid item xs={12}>
                <Alert severity="info" sx={{maxWidth: "fit-content", borderRadius: "8px"}}>
                  {t("Leave.SetEffectiveDateDescription")}
                </Alert>
              </Grid>}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography className="field-label">{t("Leave.EffectiveRound")}</Typography>
                    <Controller
                      name="effectiveYear"
                      control={useHookForm.control}
                      render={({ field, fieldState }) => (
                        <DatePickerCustom
                          {...field}
                          views={["year"]}
                          inputFormat="YYYY"
                          openTo="year"
                          value={field.value}
                          onChange={(newValue) => {
                            field.onChange(newValue);
                          }}
                          renderInput={(params) => (
                            <TextFieldTheme
                              {...params}
                              inputProps={{
                                ...params.inputProps,
                                placeholder: t("SelectDate"),
                                readOnly: true
                              }}
                              onBlur={field.onBlur}
                              error={fieldState.error? true : false}
                              helperText={fieldState.error? fieldState.error.message : null}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Fragment>
          )}

          {editModeWatch === "overwrite" && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{maxWidth: "fit-content", borderRadius: "8px"}}>
                {t("Leave.OverwriteDescription")}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Typography className="field-label">{t("LeaveName")}</Typography>
            <Controller
              name="leaveGroupName"
              control={useHookForm.control}
              render={({ field, fieldState }) => (
                <TextFieldTheme
                  {...field}
                  helperText={fieldState.error? fieldState.error.message: null}
                  error={fieldState.error? true: false}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography className="field-label">{t("LeaveName")} (EN)</Typography>
            <Controller
              name="leaveGroupName_EN"
              control={useHookForm.control}
              render={({ field, fieldState }) => (
                <TextFieldTheme
                  {...field}
                  helperText={fieldState.error? fieldState.error.message: null}
                  error={fieldState.error? true: false}
                />
              )}
            />
          </Grid>

          {useFieldArrayLeaveType.fields.map((field, index) => (
            <Grid key={field.id} item xs={12}>
              <LeaveTypeForm
                index={index}
                useHookForm={useHookForm}
                groupLevelList={groupLevelList}
                employeeTypeList={employeeTypeList}
                handleRemoveLeaveType={handleRemoveLeaveType}
                handleMoveUpLeaveType={handleMoveUpLeaveType}
                handleMoveDownLeaveType={handleMoveDownLeaveType}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <ButtonBlue
              variant="outlined"
              onClick={handleInsertLeaveType}
              startIcon={<AddCircleOutlineRoundedIcon />}
            >
              {t("AddEmployeeGroup")}
            </ButtonBlue>
          </Grid>

          <Grid item xs={12} container justifyContent="space-between">
            <ButtonBlue
              variant="text"
              onClick={onClose}
              disabled={useHookForm.formState.isSubmitting}
            >
              {t("Cancel")}
            </ButtonBlue>
            <ButtonBlue
              variant="contained"
              type="submit"
              disabled={useHookForm.formState.isSubmitting}
            >
              {t("Save")}
            </ButtonBlue>
          </Grid>
        </Grid>
      </form>
    </StyledRoot>
  )

}

export default LeaveForm;